import type { CurrentUser, Org } from './types';

/**
 * API client.
 *
 * The access token lives in memory only — never localStorage — so a script
 * injected into the page has nothing durable to steal. Continuity across
 * reloads comes from the refresh cookie, which is httpOnly and scoped to
 * /api/auth, and which the browser will hand back on the silent refresh at
 * start-up.
 */

let accessToken: string | null = null;
let refreshInFlight: Promise<boolean> | null = null;

const listeners = new Set<(token: string | null) => void>();

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
  for (const listener of listeners) listener(token);
}

/** Notified on sign-in, refresh and sign-out — the realtime socket reconnects on it. */
export function onTokenChange(listener: (token: string | null) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code = 'error',
    readonly details?: { path: string; message: string }[],
  ) {
    super(message);
  }

  /** Field-level messages, for showing validation errors next to their input. */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const detail of this.details ?? []) out[detail.path] = detail.message;
    return out;
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Raw bodies (recording uploads) bypass JSON encoding. */
  raw?: Blob | ArrayBuffer;
  contentType?: string;
  signal?: AbortSignal;
}

async function raw(path: string, options: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = {};
  if (accessToken) headers.authorization = `Bearer ${accessToken}`;
  let body: BodyInit | undefined;
  if (options.raw) {
    body = options.raw;
    headers['content-type'] = options.contentType ?? 'application/octet-stream';
  } else if (options.body !== undefined) {
    body = JSON.stringify(options.body);
    headers['content-type'] = 'application/json';
  }

  return fetch(`/api${path}`, {
    method: options.method ?? 'GET',
    headers,
    body,
    credentials: 'include',
    signal: options.signal,
  });
}

/**
 * Silently exchanges the refresh cookie for a new access token. Concurrent
 * callers share one in-flight request, so a burst of expired calls produces a
 * single rotation rather than a race that revokes the token it just issued.
 */
export async function refreshSession(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const res = await fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' });
      if (!res.ok) {
        setAccessToken(null);
        return false;
      }
      const data = (await res.json()) as { accessToken: string };
      setAccessToken(data.accessToken);
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let res = await raw(path, options);

  // One transparent retry on an expired token; anything else is the caller's problem.
  if (res.status === 401) {
    const payload = await res.clone().json().catch(() => null);
    if (payload?.error === 'token_expired' || payload?.error === 'unauthorized') {
      if (await refreshSession()) res = await raw(path, options);
    }
  }

  if (!res.ok) {
    const payload = await res.json().catch(() => null);
    throw new ApiError(
      res.status,
      payload?.message ?? `Request failed with ${res.status}`,
      payload?.error ?? 'error',
      payload?.details,
    );
  }

  if (res.status === 204) return undefined as T;
  const type = res.headers.get('content-type') ?? '';
  if (!type.includes('application/json')) return (await res.blob()) as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T,>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  post: <T,>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T,>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  put: <T,>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  del: <T,>(path: string) => request<T>(path, { method: 'DELETE' }),
  upload: <T,>(path: string, blob: Blob) =>
    request<T>(path, { method: 'POST', raw: blob, contentType: blob.type || 'audio/webm' }),
};

// ---------------------------------------------------------------- auth calls

export interface SessionResponse {
  accessToken: string;
  expiresIn: number;
  user: CurrentUser;
}

export async function login(email: string, password: string): Promise<CurrentUser> {
  const res = await request<SessionResponse>('/auth/login', {
    method: 'POST',
    body: { email, password },
  });
  setAccessToken(res.accessToken);
  return res.user;
}

export async function logout(): Promise<void> {
  await request('/auth/logout', { method: 'POST' }).catch(() => undefined);
  setAccessToken(null);
}

export interface MeResponse {
  user: CurrentUser;
  org: Org;
  env: 'production' | 'demo';
  isDemo: boolean;
}

export const fetchMe = () => api.get<MeResponse>('/auth/me');

export const changePassword = (currentPassword: string, newPassword: string) =>
  api.post<{ ok: boolean; message: string }>('/auth/change-password', { currentPassword, newPassword });
